import {ApiResponse, CartItem, Product, Proforma, SiteConfig} from "../models";
import apiClientPublic from "./api/apiClientPublic";
import {handleApiError} from "./api/apiErrorHandler";

export interface PublicGetProductsResponse extends ApiResponse {
    results: {
        products: Product[];
    };
}

export interface PublicGetProductResponse extends ApiResponse {
    results: {
        product: Product;
    };
}

export interface PublicGetEventResponse extends ApiResponse {
    results: {
        event: any;
    };
}

export interface PublicCheckoutResponse extends ApiResponse {
    results: {
        proforma: Proforma;
        paymentUrl: string;
    };
}

export interface PublicProformaStatusResponse extends ApiResponse {
    results: {
        status: 'pending' | 'paid' | 'expired';
        invoice?: {
            numberText: string;
            downloadUrl: string | null;
        } | null;
    };
}

export interface PublicClaimCompleteResponse extends ApiResponse {
    results: {
        apiAuth: {
            token: string;
            tokenType: string;
        };
    };
}

export interface PublicSiteConfigResponse extends ApiResponse {
    results: SiteConfig;
}

class ShopPublicService {
    async getProducts(): Promise<PublicGetProductsResponse> {
        return await apiClientPublic
            .get(`${this.baseUrl}/products`)
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to retrieve shop products.")
            );
    }

    async getProduct(id: string | number): Promise<PublicGetProductResponse> {
        return await apiClientPublic
            .get(`${this.baseUrl}/products/${id}`)
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to retrieve product details.")
            );
    }

    async getEvent(uuid: string): Promise<PublicGetEventResponse> {
        return await apiClientPublic
            .get(`${this.baseUrl}/events/${uuid}`)
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to retrieve event details.")
            );
    }

    async checkout(email: string, cart: CartItem[]): Promise<PublicCheckoutResponse> {
        return await apiClientPublic
            .post(`${this.baseUrl}/checkout`, {email, cart})
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to complete checkout.")
            );
    }

    async proformaStatus(uuid: string): Promise<PublicProformaStatusResponse> {
        return await apiClientPublic
            .get(`${this.baseUrl}/proformas/${uuid}/status`)
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to check proforma status.")
            );
    }

    async claimStart(email: string): Promise<ApiResponse> {
        return await apiClientPublic
            .post(`${this.baseUrl}/claim/start`, {email})
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to start claim.")
            );
    }

    async claimComplete(email: string, code: string, password: string): Promise<PublicClaimCompleteResponse> {
        return await apiClientPublic
            .post(`${this.baseUrl}/claim/complete`, {email, code, password})
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to complete claim.")
            );
    }

    async getSiteConfig(): Promise<PublicSiteConfigResponse> {
        return await apiClientPublic
            .get('/public/site-config')
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to retrieve site config.")
            );
    }

    constructor(private readonly baseUrl: string) {}
}

export const shopPublicService = new ShopPublicService('/shop-public');
